import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import ListItemIcon from '@mui/material/ListItemIcon';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';

const AutocompleteIssueTimestampHours = ({
  handleTimeSelect,
  selectedModel
}) => {
  const [issueTimeOptionsHours, setIssueTimeOptionsHours] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    let hoursToGenerate = [];

    // 🔍 DETECCIÓN DE CAPA (Lógica Robusta)
    const isHighResLayer = selectedModel && selectedModel.includes('euro_1k_00625_15m');

    if (isHighResLayer) {
      // 🔥 CORRECCIÓN: Generar horas IMPARES (1, 3, 5... 23)
      // Array de 12 elementos. Formula: i * 2 + 1
      hoursToGenerate = Array.from({ length: 12 }, (_, i) => i * 2 + 1);
    } else {
      // CASO B: Capas Estándar (025, 01) -> Solo las 4 salidas principales
      hoursToGenerate = [1, 7, 13, 19];
    }

    const times = hoursToGenerate.map((hour) => {
      // Usamos una fecha neutra para generar el string de la hora
      const time = setHours(setMinutes(setSeconds(new Date(), 0), 0), hour);
      return {
        label: `${format(time, 'HH:mm')} UTC`,
        value: format(time, 'HH:mm:ss'),
      };
    });

    setIssueTimeOptionsHours(times);

    // Lógica de Selección / Reseteo
    if (times.length > 0) {
      const currentSelectedValue = selectedOption?.value;
      const isCurrentValid = times.some(t => t.value === currentSelectedValue);

      if (!selectedOption || !isCurrentValid) {
        // Si no hay nada seleccionado, O lo que hay no existe en la nueva lista -> Reset
        const defaultOption = times[0];
        setSelectedOption(defaultOption);
        handleTimeSelect?.(defaultOption.value);
      }
    }
  }, [handleTimeSelect, selectedModel]);

  // ... (El resto de tu código visual: CustomPaper, CustomPopper y el return se quedan IGUAL)

  const CustomPaper = (props) => (
    <Paper
      {...props}
      sx={{
        backgroundColor: 'rgba(20, 20, 20, 0.85)', // Glassmorphism
        color: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mb: { xs: '0px', md: '40px' },
        ml: { xs: '60px', sm: '0px' },
        width: '200px !important', // Ajustado a 200px para consistencia

        // --- BLOQUEO DE SELECCIÓN ---
        userSelect: 'none',
        WebkitUserSelect: 'none',

        // --- AQUÍ COLOCAS EL TÍTULO ---
        '&::before': {
          content: '"SELECT ISSUE TIME"', // Título específico para este componente
          display: { xs: 'none', sm: 'block', lg: 'none' }, // Solo en sm
          padding: '12px 16px 8px',
          fontSize: '0.65rem',
          fontWeight: 800,
          color: 'rgba(255,255,255,0.5)',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          marginBottom: '4px'
        },
      }}
    />
  );

  const CustomPopper = (props) => (
    <Popper
      {...props}
      placement="top-start" // Cambiado a top para que suba sobre la barra
      sx={{
        width: '200px !important', // Fuerza el ancho del contenedor flotante
        zIndex: 5000,
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 10], // Separación de la barra
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'window',
          },
        },
      ]}
    />
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>

      <Autocomplete
        title="Click to select a different issue hours"
        options={issueTimeOptionsHours}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        value={selectedOption}

        onChange={(_, newValue) => {
          if (!newValue) return;
          setSelectedOption(newValue);
          handleTimeSelect?.(newValue.value);
        }}
        disableClearable
        selectOnFocus={false}
        forcePopupIcon
        popupIcon={
          <>
            {/* 1. LA INICIAL: Solo en sm y md */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex', lg: 'none' },
                color: '#bbb',
                fontSize: '0.7rem',
                fontWeight: 800,
                width: 32,
                height: 22,
                alignItems: 'center',
                justifyContent: 'center',

              }}
            >
              IT
            </Box>

            {/* 2. LA FLECHA: Solo en xs y lg (usamos el SVG original) */}
            <Box
              component="span"
              sx={{
                display: { xs: 'flex', sm: 'none', lg: 'flex' },
                alignItems: 'center'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#bbb">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Box>
          </>
        }
        PaperComponent={CustomPaper}
        PopperComponent={CustomPopper}
        PopperProps={{
          disablePortal: false,
          anchorEl: null,
          sx: {
            // Forzamos el ancho del panel AQUÍ, sin que el input se entere
            width: '250px !important',
            '& .MuiPaper-root': {
              width: '200px',
              backgroundColor: 'rgba(0,0,0,0.95)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }
          }
        }}
        ListboxProps={{
          sx: {
            // 2. CAMBIA ESTO: Deja el width en 100% para que use el del Popper
            width: '100%',

            '& .MuiAutocomplete-option': {
              gap: '8px',

              whiteSpace: 'nowrap', // <--- Evita que el texto salte de línea
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
            },
            '& .MuiAutocomplete-option:not(:last-child)': { mb: 0.5 },
          },
        }}

        renderOption={(props, option, { selected }) => (

          <li {...props} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <ListItemIcon sx={{ minWidth: 30 }}>
              <Radio edge="start" checked={selected} tabIndex={-1} disableRipple sx={{ color: '#bbb', '&.Mui-checked': { color: '#4caf50' }, cursor: 'pointer' }} />
            </ListItemIcon>
            <Typography variant="body1" sx={{ color: '#ffffff', fontSize: '0.85rem', fontWeight: selected ? 700 : 500 }}>
              {option.label}
            </Typography>
          </li>
        )}

        renderInput={(params) => (
          <TextField
            {...params}
            label="Issue Time"
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              readOnly: true,
              tabIndex: -1,
              onSelect: (e) => e.preventDefault(),
              style: {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }
            }}
            onFocus={(e) => {
              if (e.target) e.target.scrollLeft = 0;
            }}
            sx={{
              userSelect: 'none',
              width: 175, // <--- CAMBIADO: Antes '100%', ahora 175 como el AI Model
              cursor: 'pointer',
              '& .MuiOutlinedInput-root': {
                height: 40,
                backgroundColor: 'rgba(0,0,0,0.35)',
                color: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                backdropFilter: 'blur(2px)',
                cursor: 'pointer',
                // paddingRight eliminado para que el texto tenga el mismo espacio que el otro
                '& fieldset': { borderColor: '#1976d2' },
                '&:hover fieldset': { borderColor: '#1565c0' },
                '&.Mui-focused fieldset': { borderColor: '#1976d2' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)', '&.Mui-focused': { color: '#ffffff' } },
              '& .MuiInputBase-input': {
                color: '#fff',
                padding: '10 !important',
                fontSize: '0.85rem',
                pointerEvents: 'none',
                userSelect: 'none',
                textOverflow: 'ellipsis'
              },
              '& .MuiAutocomplete-endAdornment': {
                // CAMBIADO: md ahora es 12px para igualar al AI Model
                marginRight: { xs: '0px', sm: '0px', md: '12px', lg: '0px' },
                marginLeft: '5px',
                '& .MuiAutocomplete-popupIndicator': {
                  transform: 'none !important',
                  '&.MuiAutocomplete-popupIndicatorOpen': { transform: 'none !important' }
                },
              },
            }}
          />
        )}
        sx={{ width: 190, mt: '10px' }}
      />
    </Box>
  );
};

export default AutocompleteIssueTimestampHours;